import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Wine, Skull, ArrowRight, Building2, ArrowLeft, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TeamCard = ({ card, index, onPlan }) => {
  const palettes = [
    { accent: "#d4af37", wash: "from-[#d4af37]/15 to-transparent" },
    { accent: "#8b0000", wash: "from-[#8b0000]/15 to-transparent" },
    { accent: "#b30000", wash: "from-[#b30000]/15 to-transparent" },
  ];
  const p = palettes[index % palettes.length];
  return (
    <motion.div className="card-noir p-6 relative overflow-hidden flex flex-col"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.1 }}
      data-testid={`team-card-${card.id}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${p.wash} pointer-events-none`} />
      <div className="relative flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="font-hand text-[#d4af37] rotate-[-2deg] text-xl">team file</div>
          <div className="text-xs tracking-widest text-[#a39797]">#{card.number}</div>
        </div>
        <div className="font-display text-2xl leading-tight mb-2" style={{ color: p.accent }}>
          {card.title}
        </div>
        <div className="font-hand text-[#f4f0ec] rotate-[-1deg] text-base mb-4">{card.tagline}</div>
        <div className="text-sm text-[#a39797] leading-relaxed flex-1">{card.description}</div>
        {card.limit_note && (
          <div className="mt-3 text-[11px] text-[#d4af37] italic leading-relaxed">
            Note: {card.limit_note}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          {card.flavor_tags.map((t) => (
            <span key={t} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded-full border border-[#2d2424] text-[#a39797]">
              {t}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#2d2424]">
          <div>
            <div className="font-display text-lg text-[#f4f0ec]">{card.price_label}</div>
            <div className="text-xs text-[#a39797]">teams of {card.team_size_label}</div>
          </div>
          <button onClick={() => onPlan(card)} className="btn-primary text-sm"
            data-testid={`plan-event-${card.id}`}>
            {card.cta} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const BrandPreview = ({ brandHex, logoUrl, company }) => {
  const brand = /^#[0-9A-Fa-f]{6}$/.test(brandHex || "") ? brandHex : "#8b0000";
  const title = (company || "Your Company").toUpperCase().slice(0, 24);
  return (
    <div className="mt-4" data-testid="brand-preview">
      <div className="text-xs tracking-widest text-[#a39797] mb-2">LIVE PREVIEW</div>
      <div className="flex items-start gap-4">
        <svg
          viewBox="0 0 360 640"
          width="150"
          className="rounded-md border border-[#2d2424] shadow-[0_8px_18px_rgba(0,0,0,0.4)]"
          data-testid="brand-preview-svg"
          aria-label="Case Closed certificate preview"
        >
          <rect width="360" height="640" fill="#e8e4d9" />
          {/* Brand band */}
          <rect width="360" height="106" fill={brand} />
          <text x="20" y="42" fill="#e8e4d9" fontFamily="serif" fontWeight="bold" fontSize="32">WE SOLVED</text>
          <text x="20" y="80" fill="#d4af37" fontFamily="serif" fontWeight="bold" fontSize="22">CASE #006</text>
          {/* Logo in top-right of the band */}
          {logoUrl && (
            <>
              <rect x="260" y="18" width="80" height="70" fill="#ffffff" fillOpacity="0.9" rx="4" />
              <image href={logoUrl} x="268" y="26" width="64" height="54" preserveAspectRatio="xMidYMid meet" />
            </>
          )}
          {/* Case title + divider */}
          <text x="20" y="150" fill={brand} fontFamily="serif" fontWeight="bold" fontSize="22">{title}</text>
          <rect x="20" y="160" width="320" height="4" fill={brand} />
          {/* Victim block */}
          <text x="20" y="200" fill={brand} fontFamily="sans-serif" fontWeight="bold" fontSize="14">VICTIM</text>
          <text x="20" y="230" fill="#1a1413" fontFamily="serif" fontWeight="bold" fontSize="28">Juliet Vane</text>
          <text x="20" y="290" fill={brand} fontFamily="sans-serif" fontWeight="bold" fontSize="14">HOSTED BY</text>
          <text x="20" y="320" fill="#1a1413" fontFamily="serif" fontWeight="bold" fontSize="22">The {company || "Acme"} Table</text>
          <text x="20" y="380" fill={brand} fontFamily="sans-serif" fontWeight="bold" fontSize="14">ON</text>
          <text x="20" y="410" fill="#1a1413" fontFamily="serif" fontSize="18">February 14, 2026</text>
          {/* SOLVED stamp */}
          <rect x="80" y="470" width="200" height="60" fill="none" stroke={brand} strokeWidth="3" />
          <text x="110" y="510" fill={brand} fontFamily="serif" fontWeight="bold" fontSize="30">SOLVED</text>
          {/* Footer */}
          <text x="20" y="580" fill={brand} fontFamily="serif" fontWeight="bold" fontSize="18">wasntme.party</text>
          <text x="20" y="605" fill="#1a1413" fontFamily="serif" fontSize="10">a NightBlood Chronicles production</text>
          <text x="20" y="625" fill="#5a4641" fontFamily="serif" fontSize="9">@nightbloodchronicles</text>
        </svg>
        <div className="text-[11px] text-[#a39797] leading-relaxed flex-1">
          This is roughly what every player will post to their Story. The brand band, dividers, and SOLVED stamp will all use the hex above. If no logo is uploaded, the top-right corner stays empty.
        </div>
      </div>
    </div>
  );
};

const InquiryModal = ({ initialParty, initialPackage, onClose }) => {
  const [form, setForm] = useState({
    party: initialParty || "halloween",
    company: "", contact_name: "", work_email: "", role: "",
    people_count: 30, preferred_date: "", format: "either", package: initialPackage || "",
    personalize: "not_sure", message: "", agree_contact: true,
    // Quote-only fields (used when package === "custom")
    estimated_amount: 1999,
    line_item_description: "",
    net_days: 30,
    logo_storage_path: "",
    brand_hex: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null); // {hosted_invoice_url, amount_due_cents, ...}
  const [logoUploading, setLogoUploading] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isQuoteMode = form.package === "custom";
  const logoPreviewUrl = form.logo_storage_path
    ? `${process.env.REACT_APP_BACKEND_URL}/api/business/logo/${form.logo_storage_path}`
    : "";

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo too large. Max 2 MB.");
      e.target.value = "";
      return;
    }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/business/upload-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("logo_storage_path", r.data.storage_path);
      toast.success("Logo uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed.");
      e.target.value = "";
    } finally {
      setLogoUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree_contact) { toast.error("Please agree to be contacted about this request."); return; }
    setLoading(true);
    try {
      if (isQuoteMode) {
        const amount_cents = Math.round(parseFloat(form.estimated_amount || 0) * 100);
        if (amount_cents < 149900) {
          toast.error("Enterprise quotes start at $1,499. For smaller teams, pick a listed package.");
          setLoading(false);
          return;
        }
        const brandHex = form.brand_hex.trim();
        if (brandHex && !/^#[0-9A-Fa-f]{6}$/.test(brandHex)) {
          toast.error("Brand color must be a 6-digit hex, e.g. #8b0000");
          setLoading(false);
          return;
        }
        const description = form.line_item_description.trim()
          || `wasntme.party team event — ${form.party} · ${form.people_count} people · ${form.company}`;
        const r = await api.post("/business/quote", {
          party: form.party, company: form.company, contact_name: form.contact_name,
          work_email: form.work_email, role: form.role, people_count: parseInt(form.people_count, 10),
          preferred_date: form.preferred_date, format: form.format,
          personalize: form.personalize, message: form.message, agree_contact: form.agree_contact,
          estimated_amount_cents: amount_cents,
          line_item_description: description,
          net_days: parseInt(form.net_days, 10),
          logo_storage_path: form.logo_storage_path || "",
          brand_hex: form.brand_hex.trim() || "",
        });
        setQuoteResult(r.data);
        setDone(true);
      } else {
        await api.post("/business/inquiry", { ...form, people_count: parseInt(form.people_count, 10) });
        setDone(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="card-noir p-8 max-w-2xl w-full relative my-10" onClick={(e) => e.stopPropagation()}
        data-testid="inquiry-modal">
        {done && quoteResult ? (
          <>
            <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-2">your quote is live</div>
            <h2 className="font-display text-3xl mb-3">Stripe invoice sent.</h2>
            <p className="text-[#a39797] mb-4">
              We've emailed a Stripe invoice for <span className="text-[#f4f0ec] font-semibold">${(quoteResult.amount_due_cents / 100).toLocaleString()}</span> to{" "}
              <span className="text-[#f4f0ec]">{form.work_email}</span>. You can pay now or forward it to your billing team. NET {form.net_days} days.
            </p>
            <div className="card-noir p-4 mb-6 border-[#d4af37]/40" data-testid="quote-success-card">
              <div className="text-xs tracking-widest text-[#a39797] mb-2">INVOICE</div>
              <div className="text-sm text-[#a39797] break-all mb-3">{quoteResult.stripe_invoice_id}</div>
              {form.logo_storage_path && (
                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#2d2424]">
                  <img src={logoPreviewUrl} alt="Your logo"
                    className="w-14 h-14 object-contain rounded border border-[#2d2424] bg-[#0a0908] p-1" />
                  <div className="text-xs text-[#a39797]">Logo captured — we'll weave it into the branded kit.</div>
                </div>
              )}
              {quoteResult.hosted_invoice_url && (
                <a href={quoteResult.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                  className="btn-primary text-sm" data-testid="quote-view-invoice">
                  View &amp; pay invoice <ArrowRight size={14} />
                </a>
              )}
              {quoteResult.quote_id && (
                <div className="mt-4 pt-4 border-t border-[#2d2424]">
                  <div className="text-xs tracking-widest text-[#a39797] mb-2">SHAREABLE PREVIEW LINK</div>
                  <div className="flex flex-col sm:flex-row items-stretch gap-2">
                    <input readOnly
                      value={`${window.location.origin}/preview/${quoteResult.quote_id}`}
                      className="input-noir text-xs font-mono flex-1"
                      onFocus={(e) => e.target.select()}
                      data-testid="quote-preview-link" />
                    <a href={`/preview/${quoteResult.quote_id}`} target="_blank" rel="noopener noreferrer"
                      className="btn-ghost text-xs justify-center" data-testid="quote-preview-open">
                      Open preview <ArrowRight size={12} />
                    </a>
                  </div>
                  <div className="text-[11px] text-[#a39797] mt-2">
                    Forward to marketing to review your brand on the actual Case Closed certificate before paying.
                  </div>
                </div>
              )}
            </div>
            <button className="btn-ghost text-sm" onClick={onClose} data-testid="inquiry-close">Done</button>
          </>
        ) : done ? (
          <>
            <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-2">got it</div>
            <h2 className="font-display text-3xl mb-3">Request received.</h2>
            <p className="text-[#a39797] mb-6">Check your inbox — we sent a confirmation with a sample case link. We'll follow up with a quote within one business day.</p>
            <button className="btn-primary" onClick={onClose} data-testid="inquiry-close">Nice</button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-1">plan a team event</div>
                <h2 className="font-display text-3xl">Tell us about your party.</h2>
              </div>
              <button onClick={onClose} className="text-[#a39797] hover:text-[#f4f0ec] text-sm" data-testid="inquiry-cancel">close</button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs tracking-widest text-[#a39797] mb-2">WHICH PARTY?</label>
                <select className="input-noir" value={form.party} onChange={(e) => update("party", e.target.value)}
                  data-testid="inquiry-party">
                  <option value="halloween">Halloween — The Haunted Office</option>
                  <option value="christmas">Christmas — Secret Santa Showdown</option>
                  <option value="new_years">New Year's — Countdown Killer</option>
                  <option value="summer">Summer / Fourth of July — Cookout Caper</option>
                  <option value="inventory">Inventory Day — Inventory Investigation</option>
                  <option value="team_pass">Team Pass (4 events / year)</option>
                  <option value="other">Something else</option>
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">COMPANY NAME</label>
                  <input className="input-noir" required value={form.company}
                    onChange={(e) => update("company", e.target.value)} data-testid="inquiry-company" />
                </div>
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">YOUR NAME</label>
                  <input className="input-noir" required value={form.contact_name}
                    onChange={(e) => update("contact_name", e.target.value)} data-testid="inquiry-contact-name" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">WORK EMAIL</label>
                  <input type="email" className="input-noir" required value={form.work_email}
                    onChange={(e) => update("work_email", e.target.value)} data-testid="inquiry-email" />
                </div>
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">YOUR ROLE (optional)</label>
                  <input className="input-noir" value={form.role}
                    onChange={(e) => update("role", e.target.value)} data-testid="inquiry-role" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">NUMBER OF PEOPLE</label>
                  <input type="number" min={1} max={5000} className="input-noir" required value={form.people_count}
                    onChange={(e) => update("people_count", e.target.value)} data-testid="inquiry-people-count" />
                </div>
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">PREFERRED DATE</label>
                  <input className="input-noir" placeholder="e.g. Oct 29 or 'not sure'" value={form.preferred_date}
                    onChange={(e) => update("preferred_date", e.target.value)} data-testid="inquiry-date" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">FORMAT</label>
                  <select className="input-noir" value={form.format} onChange={(e) => update("format", e.target.value)}
                    data-testid="inquiry-format">
                    <option value="in_person">In person</option>
                    <option value="virtual">Virtual</option>
                    <option value="either">Either</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs tracking-widest text-[#a39797] mb-2">PACKAGE</label>
                  <select className="input-noir" value={form.package} onChange={(e) => update("package", e.target.value)}
                    data-testid="inquiry-package">
                    <option value="">Not sure</option>
                    <option value="starter">Starter — up to 15 ($249)</option>
                    <option value="team">Team — up to 30 ($449)</option>
                    <option value="department">Department — up to 60 ($799)</option>
                    <option value="company">Company — up to 150 ($1,499)</option>
                    <option value="custom">Custom / 150+ (quote)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs tracking-widest text-[#a39797] mb-2">ANYTHING ELSE?</label>
                <textarea className="input-noir min-h-[100px]" value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Off-limits topics, accessibility needs, logo, inside jokes…"
                  data-testid="inquiry-message" />
              </div>
              {isQuoteMode && (
                <div className="card-noir p-5 border-[#d4af37]/40 relative" data-testid="quote-fields">
                  <div className="absolute -top-3 left-4 bg-[#8b0000] text-[#f4f0ec] text-[10px] tracking-[0.18em] uppercase px-2 py-[2px] rounded-sm border border-[#d4af37]">
                    Enterprise · Stripe invoice
                  </div>
                  <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg mb-1 mt-1">Request a quote</div>
                  <p className="text-xs text-[#a39797] mb-4">
                    We'll generate a real Stripe invoice ($1,499+) emailed to you. Pay by card, ACH, or forward the hosted link to your billing team.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs tracking-widest text-[#a39797] mb-2">ESTIMATED BUDGET (USD)</label>
                      <input type="number" min={1499} step={100} className="input-noir" required value={form.estimated_amount}
                        onChange={(e) => update("estimated_amount", e.target.value)} data-testid="quote-amount" />
                      <div className="text-[11px] text-[#a39797] mt-1">Minimum $1,499 · adjusts on the final invoice if scope changes.</div>
                    </div>
                    <div>
                      <label className="block text-xs tracking-widest text-[#a39797] mb-2">PAYMENT TERMS</label>
                      <select className="input-noir" value={form.net_days}
                        onChange={(e) => update("net_days", e.target.value)} data-testid="quote-net-days">
                        <option value={0}>Due on receipt</option>
                        <option value={15}>NET 15</option>
                        <option value={30}>NET 30</option>
                        <option value={60}>NET 60</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-xs tracking-widest text-[#a39797] mb-2">LINE ITEM DESCRIPTION (optional)</label>
                    <textarea className="input-noir min-h-[70px]" value={form.line_item_description}
                      onChange={(e) => update("line_item_description", e.target.value)}
                      placeholder="e.g. Haunted Office — custom 180-person virtual event, logo integration, 2 facilitated breakouts"
                      data-testid="quote-line-item" />
                    <div className="text-[11px] text-[#a39797] mt-1">We'll default to a sensible description if left blank.</div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-xs tracking-widest text-[#a39797] mb-2">COMPANY LOGO (optional)</label>
                    <div className="flex items-start gap-4">
                      {form.logo_storage_path ? (
                        <div className="relative">
                          <img src={logoPreviewUrl} alt="Logo preview"
                            className="w-24 h-24 object-contain rounded-md border border-[#2d2424] bg-[#0a0908] p-2"
                            data-testid="quote-logo-preview" />
                          <button type="button"
                            onClick={() => update("logo_storage_path", "")}
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#8b0000] text-[#f4f0ec] text-xs flex items-center justify-center border border-[#d4af37] hover:bg-[#b30000]"
                            aria-label="Remove logo"
                            data-testid="quote-logo-remove">×</button>
                        </div>
                      ) : (
                        <label className="w-24 h-24 rounded-md border border-dashed border-[#2d2424] bg-[#0a0908] flex flex-col items-center justify-center text-[10px] text-[#a39797] cursor-pointer hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
                          data-testid="quote-logo-dropzone">
                          <input type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                            onChange={handleLogoChange}
                            className="hidden"
                            disabled={logoUploading}
                            data-testid="quote-logo-input" />
                          <span className="text-[20px] mb-1">{logoUploading ? "…" : "+"}</span>
                          <span className="tracking-[0.14em] uppercase">{logoUploading ? "Uploading" : "Upload"}</span>
                        </label>
                      )}
                      <div className="text-[11px] text-[#a39797] leading-relaxed flex-1">
                        We'll weave it into the kit's intro slide, nameplates, and the Case Closed certificate. PNG, JPG, WebP, SVG or GIF. Max 2 MB.
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-xs tracking-widest text-[#a39797] mb-2">BRAND COLOR (optional)</label>
                    <div className="flex items-center gap-3">
                      <input type="color"
                        value={form.brand_hex || "#8b0000"}
                        onChange={(e) => update("brand_hex", e.target.value)}
                        className="w-12 h-10 rounded-md border border-[#2d2424] bg-transparent cursor-pointer"
                        aria-label="Pick brand color"
                        data-testid="quote-brand-color-picker" />
                      <input type="text"
                        value={form.brand_hex}
                        onChange={(e) => update("brand_hex", e.target.value)}
                        placeholder="#8b0000"
                        pattern="^#[0-9A-Fa-f]{6}$"
                        maxLength={7}
                        className="input-noir flex-1 font-mono text-sm uppercase"
                        data-testid="quote-brand-color-hex" />
                      {form.brand_hex && (
                        <button type="button"
                          onClick={() => update("brand_hex", "")}
                          className="text-[11px] text-[#a39797] hover:text-[#f4f0ec]"
                          data-testid="quote-brand-color-clear">clear</button>
                      )}
                    </div>
                    <div className="text-[11px] text-[#a39797] mt-1">
                      Headers, dividers, and the certificate band will use this hex instead of the default gothic red.
                    </div>
                  </div>
                  <BrandPreview brandHex={form.brand_hex} logoUrl={logoPreviewUrl} company={form.company} />
                </div>
              )}
              <label className="flex items-start gap-3 text-sm text-[#a39797] cursor-pointer">
                <input type="checkbox" required checked={form.agree_contact}
                  onChange={(e) => update("agree_contact", e.target.checked)}
                  className="mt-1 accent-[#8b0000]" data-testid="inquiry-agree" />
                <span>{isQuoteMode ? "I agree to receive a Stripe invoice at this email." : "I agree to be contacted about this request."}</span>
              </label>
              <div className="pt-2 flex items-center justify-end gap-3">
                <button type="button" className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
                <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50"
                  data-testid="inquiry-submit">
                  {loading ? "Sending…" : (isQuoteMode ? "Request Stripe invoice" : "Send request")}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const FAQ_ITEMS = [
  { q: "How fast do we get it?", a: "Stock kits arrive in your inbox right after checkout. Personalized kits are delivered after a short review — usually within one business day." },
  { q: "Do we need a facilitator?", a: "No. The host guide has the full script, and any team member can read it aloud. Virtual events include a breakout-room guide." },
  { q: "Can remote teams play?", a: "Yes. Every team case includes a Virtual Mode guide covering breakout rooms, shared screens, and how teams submit answers." },
  { q: "How many people can play?", a: "The Haunted Office works from 6 up to 100+. The Countdown Killer scales to 200+. Secret Santa currently runs 6–20 per game." },
  { q: "Is it appropriate for work?", a: "Every suspect is fictional. No HR topics, no real colleagues accused, no pay or personal lives involved. Volunteering as a suspect is always optional." },
  { q: "Can you use our company name or logo?", a: "The personalization add-on lets us weave your company name and inside jokes into the kit. Logo and brand colors are a separate add-on." },
  { q: "Do you offer invoices or purchase orders?", a: "Yes — tell us in the inquiry form and we'll send a Stripe invoice with your PO number and company billing details." },
];

const ForTeams = () => {
  const navigate = useNavigate();
  const [teamCards, setTeamCards] = useState([]);
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [inquiryParty, setInquiryParty] = useState(null);
  const [inquiryPackage, setInquiryPackage] = useState("");

  useEffect(() => {
    api.get("/business/products").then((r) => {
      setTeamCards(r.data.team_cards);
      setPackages(r.data.packages);
      setAddons(r.data.addons);
    }).catch(() => {});
  }, []);

  const handlePlan = (card) => {
    // T02 has a dedicated guide page; everything else opens the inquiry modal
    if (card.id === "T02") {
      navigate("/for-teams/secret-santa-office");
    } else {
      setInquiryParty(card.party);
    }
  };

  return (
    <div className="grain min-h-screen relative" data-testid="for-teams-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#0a0908]/80 border-b border-[#2d2424]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display text-xl tracking-wide" data-testid="brand-logo">
            <Wine size={18} className="text-[#8b0000]" />
            <span>wasntme<span className="text-[#8b0000]">.party</span></span>
          </Link>
          <Link to="/" className="btn-ghost text-sm" data-testid="back-to-consumer">
            <ArrowLeft size={14} /> Back to cases
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="max-w-5xl mx-auto px-6">
          <div className="font-hand text-[#d4af37] text-2xl rotate-[-2deg] mb-4">for teams &amp; venues</div>
          <h1 className="font-display text-5xl sm:text-6xl font-bold leading-[0.95] tracking-tight max-w-3xl">
            The team party that <span className="text-[#8b0000]">runs itself</span>.
          </h1>
          <p className="mt-6 text-lg text-[#a39797] max-w-2xl leading-relaxed">
            A murder mystery for your office Halloween, holiday party, year-end lunch, summer picnic, or inventory day. Instant download, no facilitator needed, safe for work, in person or virtual.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => setInquiryParty("halloween")} className="btn-primary" data-testid="hero-plan-cta">
              <Building2 size={18} /> Plan a team event
            </button>
            <a href="#packages" className="btn-ghost text-sm" data-testid="hero-pricing-link">
              See pricing
            </a>
          </div>
        </div>
      </section>

      {/* Pricing strip */}
      <section id="packages" className="py-10 border-t border-[#2d2424]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="card-noir p-6 relative overflow-hidden" data-testid="pricing-strip">
            <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/10 to-transparent pointer-events-none" />
            <div className="relative grid grid-cols-2 md:grid-cols-5 gap-4">
              {packages.map((pkg) => (
                <div key={pkg.id} className="text-center py-2 border-l first:border-l-0 border-[#2d2424]"
                  data-testid={`pricing-${pkg.id}`}>
                  <div className="text-xs tracking-widest text-[#a39797] uppercase mb-1">{pkg.label}</div>
                  <div className="font-display text-2xl text-[#f4f0ec]">{pkg.price_label}</div>
                  <div className="text-xs text-[#a39797] mt-1">{pkg.people}</div>
                </div>
              ))}
            </div>
            {addons.length > 0 && (
              <div className="relative mt-6 pt-4 border-t border-[#2d2424] flex flex-wrap items-center gap-4 justify-center text-sm">
                <span className="text-xs tracking-widest text-[#a39797]">ADD-ONS:</span>
                {addons.map((a) => (
                  <span key={a.id} className="text-[#a39797]">
                    <span className="text-[#f4f0ec]">{a.label}</span> {a.price_label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Team cards */}
      <section className="py-16 border-t border-[#2d2424]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-4xl mb-10">Pick your <span className="text-[#8b0000]">team event</span>.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {teamCards.map((card, i) => (
              <TeamCard key={card.id} card={card} index={i}
                onPlan={handlePlan} />
            ))}
            {teamCards.length === 0 && (
              <div className="col-span-3 text-center text-[#a39797] py-12">Loading team cases…</div>
            )}
          </div>
          <div className="mt-10 text-center">
            <div className="text-[#a39797] mb-4">Need something different? Tell us about your event and we'll recommend a case.</div>
            <button onClick={() => { setInquiryPackage("custom"); setInquiryParty("other"); }} className="btn-primary" data-testid="request-quote-cta">
              <Building2 size={16} /> Request a quote
            </button>
          </div>
        </div>
      </section>

      {/* How it works (3-step) */}
      <section className="py-20 border-t border-[#2d2424]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="font-hand text-[#d4af37] text-xl mb-2 rotate-[-1deg]">how it works</div>
          <h2 className="font-display text-4xl mb-12">Three steps. One <span className="text-[#8b0000]">unforgettable</span> party.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Pick your party and size", desc: "Choose Halloween, Christmas, New Year's, Summer, or Inventory. Then the package that fits your headcount." },
              { title: "Personalize it (optional)", desc: "Tell us the beloved office object, inside jokes, and your team names. We weave them into the kit." },
              { title: "Download and play", desc: "Your kit arrives by email. Print it or run it on screen. Any team member can host — the script is in the box." },
            ].map((s, i) => (
              <div key={s.title} className="card-noir p-6" data-testid={`how-step-${i + 1}`}>
                <div className="text-xs text-[#a39797] tracking-widest mb-2">STEP {i + 1}</div>
                <div className="font-display text-xl mb-2">{s.title}</div>
                <div className="text-sm text-[#a39797] leading-relaxed">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safe-for-work promise */}
      <section className="py-16 border-t border-[#2d2424]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="font-hand text-[#d4af37] text-xl mb-2 rotate-[-1deg]">our promise</div>
          <h2 className="font-display text-3xl mb-6">Every case is <span className="text-[#8b0000]">safe for work</span>.</h2>
          <ul className="space-y-3 text-[#a39797]">
            {[
              "Every suspect is a made-up character. Nobody on your team is accused of anything real.",
              "No HR topics, no real performance talk, nothing about pay or personal lives.",
              "No alcohol is needed to play.",
              "Taking part as a suspect is always optional. There is a quiet way to join as an observer.",
              "The language is inclusive and works for any team.",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <Check size={16} className="text-[#d4af37] mt-1 flex-shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Deadlines */}
      <section className="py-16 border-t border-[#2d2424]" data-testid="deadlines">
        <div className="max-w-4xl mx-auto px-6">
          <div className="font-hand text-[#d4af37] text-xl mb-2 rotate-[-1deg]">plan ahead</div>
          <h2 className="font-display text-3xl mb-8">Order deadlines.</h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            {[
              { party: "Halloween (Oct 31)", stock: "Instant", custom: "Order personalized kits by Oct 26" },
              { party: "Christmas (Dec 25)", stock: "Instant", custom: "Order personalized kits by Dec 14" },
              { party: "New Year's Eve (Dec 31)", stock: "Instant", custom: "Order personalized kits by Dec 21" },
              { party: "Summer / Fourth of July", stock: "Instant", custom: "Order personalized kits by Jun 21" },
              { party: "Inventory day", stock: "Instant", custom: "Allow 5 business days for personalization" },
            ].map((d) => (
              <div key={d.party} className="card-noir p-4" data-testid={`deadline-${d.party.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Clock size={14} className="text-[#d4af37]" />
                  <div className="font-display text-lg">{d.party}</div>
                </div>
                <div className="text-xs text-[#a39797]">Stock: {d.stock} · Personalized: {d.custom}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 border-t border-[#2d2424]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="font-hand text-[#d4af37] text-xl mb-2 rotate-[-1deg]">questions</div>
          <h2 className="font-display text-3xl mb-8">Frequently asked.</h2>
          <div className="space-y-4" data-testid="faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <div key={item.q} className="card-noir p-5" data-testid={`faq-${i}`}>
                <div className="font-display text-lg mb-2 text-[#f4f0ec]">{item.q}</div>
                <div className="text-sm text-[#a39797] leading-relaxed">{item.a}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <button onClick={() => setInquiryParty("other")} className="btn-primary" data-testid="faq-cta">
              <Building2 size={16} /> Plan a team event
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#2d2424] py-10 text-center text-sm text-[#a39797]">
        <div className="font-display text-2xl text-[#f4f0ec]">wasntme<span className="text-[#8b0000]">.party</span></div>
        <div className="mt-1 text-xs tracking-[0.2em] text-[#d4af37]">A NIGHTBLOOD CHRONICLES PRODUCTION</div>
        <div className="mt-4">
          <Link to="/" className="text-[#a39797] hover:text-[#f4f0ec] transition-colors">← Back to the consumer cases</Link>
        </div>
      </footer>

      {inquiryParty && <InquiryModal initialParty={inquiryParty} initialPackage={inquiryPackage}
        onClose={() => { setInquiryParty(null); setInquiryPackage(""); }} />}
    </div>
  );
};

export default ForTeams;
